import { useLocation } from 'react-router-dom'
import { useQuery } from '../../../routes'
import { humanize, useDebounce } from 'utils'
import { flat } from 'utils'
import { useProfileMatchSearch } from '../../../queries/useProfileSearch'
import { ProfileMatch } from '../../../queries/types'
import { useState, useEffect, useMemo } from 'react'

/**
 * Custom hook for paginated admin directory search using the new useProfileSearch hook
 * with support for temporary filter state and adminSearch=true
 */
export const usePaginatedAdminSearch = () => {
  // Parse query parameters from URL
  const query = useQuery<{
    page?: number
    appliedFacets?: string[]
    queryString?: string
    excludeId?: string
  }>()

  const { search: locationSearch } = useLocation()

  // Ensure we get arrays even if the URL params are unexpected types
  const safeAppliedFacets = () => {
    const facets = query.search.appliedFacets
    return Array.isArray(facets) ? facets : [facets ?? '']
  }

  // Temporary state for filters and query that will only be applied when the user clicks "Apply"
  const [tempFacets, setTempFacets] = useState<string[]>(safeAppliedFacets())
  const [tempQueryString, setTempQueryString] = useState<string>(
    query.search.queryString || ''
  )

  // Applied state that's reflected in the URL and used for API calls
  const appliedFacets = safeAppliedFacets()
  const queryString = query.search.queryString || ''

  // Update temp state when URL params change (e.g., on browser navigation)
  useEffect(() => {
    setTempFacets(safeAppliedFacets())
    setTempQueryString(query.search.queryString || '')
  }, [locationSearch])

  // Check if there are unapplied changes
  const hasUnappliedChanges = useMemo(() => {
    // Ensure we're working with arrays
    const tempFacetsArray = Array.isArray(tempFacets) ? tempFacets : []
    const appliedFacetsArray = Array.isArray(appliedFacets) ? appliedFacets : []

    // Check query string differences
    if ((tempQueryString || '') !== (queryString || '')) return true

    // Check facet count differences
    if (tempFacetsArray.length !== appliedFacetsArray.length) return true

    // Check if any facets are different
    const appliedSet = new Set(appliedFacetsArray)
    return tempFacetsArray.some((facet) => !appliedSet.has(facet))
  }, [tempFacets, appliedFacets, tempQueryString, queryString])

  // Handle toggling facets in temporary state
  const toggleFacet = (facet: string, action: 'remove' | 'add') => {
    // Ensure we're working with an array
    const safeTempFacets = Array.isArray(tempFacets) ? tempFacets : []

    // Simple, direct implementation without using flat()
    let updatedFacets
    if (action === 'remove') {
      // Remove the facet
      updatedFacets = safeTempFacets.filter((f) => f !== facet)
    } else {
      // Add the facet if it doesn't already exist
      if (!safeTempFacets.includes(facet)) {
        updatedFacets = [...safeTempFacets, facet]
      } else {
        updatedFacets = [...safeTempFacets] // Keep as is if already exists
      }
    }

    setTempFacets(updatedFacets)
  }

  // Update temporary search query string and auto-apply with debounce
  const setTempSearchQuery = (newQueryString: string) => {
    setTempQueryString(newQueryString)
  }

  // Debounce the temporary query string for auto-application
  const debouncedTempQuery = useDebounce(tempQueryString, 450)

  // Auto-apply search when debounced query changes
  useEffect(() => {
    if (debouncedTempQuery !== queryString) {
      query.set({
        ...query.search,
        queryString: debouncedTempQuery || '',
        page: 1, // Reset to first page when search changes
      })
    }
  }, [debouncedTempQuery, queryString, query])

  // Auto-apply filter changes immediately (no debounce needed for facets)
  useEffect(() => {
    const safeTempFacets = Array.isArray(tempFacets) ? tempFacets : []
    const safeAppliedFacets = Array.isArray(appliedFacets) ? appliedFacets : []

    // Check if facets have actually changed
    if (
      safeTempFacets.length !== safeAppliedFacets.length ||
      safeTempFacets.some((facet) => !safeAppliedFacets.includes(facet))
    ) {
      query.set({
        ...query.search,
        appliedFacets: safeTempFacets,
        page: 1, // Reset to first page when filters change
      })
    }
  }, [tempFacets, appliedFacets, query])

  // Apply filters to URL and trigger search
  const applyFilters = () => {
    // Ensure we're sending valid data to URL
    const safeTempFacets = Array.isArray(tempFacets) ? tempFacets : []

    query.set({
      ...query.search,
      appliedFacets: safeTempFacets,
      queryString: tempQueryString || '',
      page: 1, // Reset to first page when applying new filters
    })
  }

  // Reset filters to match URL state
  const resetFilters = () => {
    setTempFacets(safeAppliedFacets())
    setTempQueryString(query.search.queryString || '')
  }

  // Clear all filters (both temp and applied)
  const clearAllFilters = () => {
    // Clear temporary state
    setTempFacets([])
    setTempQueryString('')

    // Clear applied state (URL parameters)
    query.set({
      page: 1,
      appliedFacets: [],
      queryString: '',
    })
  }

  // Debounce applied query for API calls
  const requestedQuery = useDebounce(queryString, 450)

  // Build facet filters from applied facets
  const facetFilters = (
    Array.isArray(appliedFacets) ? appliedFacets : [appliedFacets]
  ).filter((f) => typeof f === 'string' && f.trim() !== '')

  const searchParams = {
    query: requestedQuery,
    facet_filters: facetFilters,
    page: (query.search.page ?? 1) - 1, // Convert from 1-indexed to 0-indexed
    limit: 20,
    adminSearch: true,
    sort_by: 'createdAt',
  }

  // Use our profile search hook with the applied (not temporary) filters
  const { data, isLoading: loading, error } = useProfileMatchSearch(
    searchParams
  )

  // Extract data for the view
  // Filter out excluded profile (used after deletion to hide profile until Algolia syncs)
  const excludeId = query.search.excludeId
  const profiles: ProfileMatch[] = (data?.items || []).filter(
    (p) => !excludeId || p.id !== excludeId
  )
  const currentPage = (data?.page ?? 0) + 1 // Convert from 0-indexed to 1-indexed
  const pageCount = data?.pages ?? 0
  const success = !!data
  const facets = data?.facets || {}

  // Handle page changes
  const handlePageClick = ({ historyPush }: { historyPush?: boolean }) => (
    page: number
  ) => {
    query.set({ ...query.search, page }, historyPush)
  }

  // Format applied facets for display (using the applied facets, not temporary)
  console.log('appliedFacets', appliedFacets)
  const humanizedAppliedFacets = useMemo(() => {
    try {
      const safeArray = Array.isArray(appliedFacets)
        ? appliedFacets
        : [appliedFacets]
      return flat([safeArray], 2)
        .map((f) => {
          // Ensure f is a string before splitting
          if (typeof f !== 'string') return ['', '']
          return f.split(':')
        })
        .map((f) => ({
          id: f.join(':'),
          value:
            humanize(f[0] || '') + (f[1] ? ': ' + humanize(f[1] || '') : ''),
        }))
        .filter((f) => f.value !== '')
    } catch (e) {
      console.warn('Error formatting facets:', e)
      return []
    }
  }, [appliedFacets])

  return {
    // Applied state (from URL)
    appliedFacets,
    queryString,

    // Temporary state
    tempFacets,
    tempQueryString,
    hasUnappliedChanges,

    // Actions
    toggleFacet,
    setTempSearchQuery,
    applyFilters,
    resetFilters,
    clearAllFilters,

    // Search results
    currentPage,
    pageCount,
    error,
    facets,
    loading,
    profiles,
    search: query.search,
    handlePageClick,
    success,
    locationSearch,
    humanizedAppliedFacets,
  }
}
